// apps/web/src/components/auth/login-form.tsx — Fase 01, Plano 35.
//
// Formulário de login/MFA fiel ao contrato da UI-SPEC: seis estados
// (idle/submitting/success/error/locked/MFA-required), teclado, copy
// aprovada, toggle de senha sempre visível e nenhum dado sensível em Web
// Storage. A sessão vive só no cookie HttpOnly emitido pela API (ADR-003).
import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { calcularDestinoPosLogin, limparRetornoPendente, obterRetornoPendente } from '../../lib/destino-pos-login';
import { obterSessaoAtual } from '../../lib/session';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
const CSRF_HEADER_NAME = 'x-csrf-token';
const LOCKOUT_ATTEMPTS = 5; // o 6º submit exibe a mensagem de bloqueio, espelhando o servidor

type LoginPhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'submitting-credentials' }
  | { readonly kind: 'mfa-required'; readonly challengeId: string; readonly challengeToken: string; readonly error?: string }
  | { readonly kind: 'submitting-mfa'; readonly challengeId: string; readonly challengeToken: string }
  | { readonly kind: 'navigating'; readonly role?: string }
  | { readonly kind: 'error'; readonly message: string; readonly locked: boolean };

/** Lê as permissões da sessão recém-criada para escolher a página inicial; sem elas, usa a regra pelo perfil. */
async function navegarPosLogin(role?: string): Promise<void> {
  const sessao = await obterSessaoAtual();
  const permissoes = sessao.autenticado ? sessao.usuario.permissions : undefined;
  const retornoPendente = obterRetornoPendente();
  const destino = calcularDestinoPosLogin(role, retornoPendente, permissoes);
  limparRetornoPendente();
  if (typeof window !== 'undefined') {
    window.location.assign(destino);
  }
}

interface CsrfResponse {
  readonly csrfToken: string;
}

interface LoginResponse {
  readonly ok: boolean;
  readonly mfaRequired?: boolean;
  readonly challengeId?: string;
  readonly challengeToken?: string;
  readonly user?: { readonly id: string; readonly username: string; readonly role: string };
}

interface MfaResponse {
  readonly ok: boolean;
  readonly user?: { readonly id: string; readonly username: string; readonly role: string };
}

interface ApiError {
  readonly statusCode: number;
  readonly message: string;
}

class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly statusCode: number,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

async function fetchCsrfToken(): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/csrf`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Não foi possível preparar o formulário.');
  }
  const body = (await response.json()) as CsrfResponse;
  return body.csrfToken;
}

async function apiPost<T>(path: string, csrfToken: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      [CSRF_HEADER_NAME]: csrfToken,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const error = (await response.json().catch(() => ({}))) as Partial<ApiError>;
    throw new ApiRequestError(error.message ?? 'Erro ao processar a requisição.', response.status);
  }
  return (await response.json()) as T;
}

export function LoginForm(): ReactElement {
  const [phase, setPhase] = useState<LoginPhase>({ kind: 'idle' });
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [useRecovery, setUseRecovery] = useState(false);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const usernameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchCsrfToken()
      .then(setCsrfToken)
      .catch(() => setPhase({ kind: 'error', message: 'Não foi possível preparar o formulário.', locked: false }));
  }, []);

  useEffect(() => {
    // Foco inicial no campo de usuário para acessibilidade/teclado.
    if (phase.kind === 'idle') {
      usernameRef.current?.focus();
    }
  }, [phase.kind]);

  const isLocked = failedAttempts >= LOCKOUT_ATTEMPTS;

  async function handleCredentialSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (isLocked) {
      return;
    }
    setPhase({ kind: 'submitting-credentials' });
    let token = csrfToken;
    if (!token) {
      try {
        token = await fetchCsrfToken();
        setCsrfToken(token);
      } catch {
        setPhase({ kind: 'error', message: 'Não foi possível preparar o formulário.', locked: false });
        return;
      }
    }
    try {
      const result = await apiPost<LoginResponse>('/api/v1/auth/login', token, {
        username,
        password,
      });
      if (result.mfaRequired && result.challengeId && result.challengeToken) {
        setPhase({ kind: 'mfa-required', challengeId: result.challengeId, challengeToken: result.challengeToken });
        setTotp('');
        setRecoveryCode('');
        return;
      }
      setPhase({ kind: 'navigating', role: result.user?.role });
      await navegarPosLogin(result.user?.role);
    } catch (err) {
      // Só credenciais realmente rejeitadas pelo backend (401 uniforme)
      // incrementam o contador local de bloqueio — falhas de rede, erro do
      // servidor (5xx) ou de preparo do CSRF não são "senha errada" e não
      // devem contar para o bloqueio client-side nem exibir essa mensagem.
      if (err instanceof ApiRequestError && err.statusCode === 401) {
        const nextAttempts = failedAttempts + 1;
        setFailedAttempts(nextAttempts);
        const locked = nextAttempts >= LOCKOUT_ATTEMPTS;
        setPhase({
          kind: 'error',
          message: locked
            ? 'Muitas tentativas de login. Aguarde alguns minutos e tente novamente.'
            : 'Usuário ou senha incorretos. Verifique os dados e tente novamente.',
          locked,
        });
        return;
      }
      if (err instanceof ApiRequestError && (err.statusCode === 429 || err.statusCode === 423)) {
        setPhase({
          kind: 'error',
          message: 'Muitas tentativas de login. Aguarde alguns minutos e tente novamente.',
          locked: true,
        });
        return;
      }
      setPhase({
        kind: 'error',
        message: 'Não foi possível concluir o login. Verifique sua conexão e tente novamente.',
        locked: false,
      });
    }
  }

  async function handleMfaSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (phase.kind !== 'mfa-required' && phase.kind !== 'submitting-mfa') {
      return;
    }
    const challengeId = phase.challengeId;
    const challengeToken = phase.challengeToken;
    setPhase({ kind: 'submitting-mfa', challengeId, challengeToken });
    let token = csrfToken;
    if (!token) {
      try {
        token = await fetchCsrfToken();
        setCsrfToken(token);
      } catch {
        setPhase({
          kind: 'mfa-required',
          challengeId,
          challengeToken,
          error: 'Não foi possível preparar o formulário.',
        });
        return;
      }
    }
    try {
      const result = await apiPost<MfaResponse>('/api/v1/auth/mfa/verify', token, {
        challengeId,
        challengeToken,
        totp: useRecovery ? undefined : totp,
        recoveryCode: useRecovery ? recoveryCode : undefined,
      });
      if (result.ok) {
        setPhase({ kind: 'navigating', role: result.user?.role });
        await navegarPosLogin(result.user?.role);
      } else {
        setPhase({
          kind: 'mfa-required',
          challengeId,
          challengeToken,
          error: 'Código inválido ou expirado. Gere um novo código no aplicativo autenticador e tente novamente.',
        });
      }
    } catch {
      setPhase({
        kind: 'mfa-required',
        challengeId,
        challengeToken,
        error: 'Código inválido ou expirado. Gere um novo código no aplicativo autenticador e tente novamente.',
      });
    }
  }


  if (phase.kind === 'navigating') {
    return (
      <div role="status" aria-live="polite" className="login-form__success">
        <h2 className="login-form__heading">Autenticado com sucesso</h2>
        <p>Redirecionando para o sistema...</p>
      </div>
    );
  }

  const isSubmitting = phase.kind === 'submitting-credentials' || phase.kind === 'submitting-mfa';
  const isMfa = phase.kind === 'mfa-required' || phase.kind === 'submitting-mfa';
  const alerta =
    phase.kind === 'error'
      ? phase.message
      : phase.kind === 'mfa-required'
        ? phase.error
        : undefined;

  return (
    <form
      onSubmit={(event) => void (isMfa ? handleMfaSubmit(event) : handleCredentialSubmit(event))}
      className="login-form"
      aria-busy={isSubmitting}
      noValidate
    >
      {alerta && (
        <div role="alert" className="ui-form-message login-form__alert">
          {alerta}
        </div>
      )}

      {isMfa && (
        <h2 className="login-page__subtitle">Verificação em duas etapas</h2>
      )}

      {!isMfa ? (
        <>
          <div className="login-form__field">
            <label htmlFor="login-username" className="login-form__label">
              Usuário
            </label>
            <input
              id="login-username"
              ref={usernameRef}
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="ui-input ui-control"
              disabled={isSubmitting || isLocked}
              required
            />
          </div>
          <div className="login-form__field">
            <label htmlFor="login-password" className="login-form__label">
              Senha
            </label>
            <div className="login-form__password-wrapper">
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="ui-input ui-control"
                disabled={isSubmitting || isLocked}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? 'Ocultar caracteres' : 'Exibir caracteres'}
                className="ui-button ui-button--secondary login-form__password-toggle"
                disabled={isSubmitting || isLocked}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            className="ui-button ui-button--primary ui-button--full-width"
            disabled={isSubmitting || isLocked}
          >
            {phase.kind === 'submitting-credentials' ? (
              <>
                <Loader2 size={18} aria-hidden="true" className="login-form__spinner" /> Entrando…
              </>
            ) : (
              'Entrar'
            )}
          </button>
        </>
      ) : (
        <>
          <p className="login-form__hint">Insira o código gerado pelo aplicativo autenticador.</p>
          <div className="login-form__field">
            <label htmlFor="login-totp" className="login-form__label">
              {useRecovery ? 'Código de recuperação' : 'Código de 6 dígitos'}
            </label>
            <input
              id="login-totp"
              type={useRecovery ? 'text' : 'text'}
              inputMode={useRecovery ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              value={useRecovery ? recoveryCode : totp}
              onChange={(e) => (useRecovery ? setRecoveryCode(e.target.value) : setTotp(e.target.value))}
              className="ui-input ui-control"
              style={{ fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)' }}
              disabled={isSubmitting}
              required
            />
          </div>
          <button
            type="button"
            onClick={() => setUseRecovery((value) => !value)}
            className="login-form__link"
            disabled={isSubmitting}
          >
            {useRecovery ? 'Usar código do autenticador' : 'Usar código de recuperação'}
          </button>
          <button
            type="submit"
            className="ui-button ui-button--primary ui-button--full-width"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 size={18} aria-hidden="true" className="login-form__spinner" /> Verificando…
              </>
            ) : (
              'Verificar código'
            )}
          </button>
        </>
      )}
    </form>
  );
}
